import { VestingCard } from '@dealstack/frontend/shared/components/VestingCard/VestingCard.tsx';
import { IconClockHour3 } from '@tabler/icons-react';
import { Flex, NumberInput, Text } from '@mantine/core';
import { VestingTermType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { StraightLineTerm as StraightLine } from '@dealstack/frontend/shared/components/VestingTerms/StraightLineTerm/StraightLineTerm.tsx';
import { GradedTerms } from '@dealstack/frontend/shared/components/VestingTerms/GradedTerms.tsx';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useVestingTermsContext } from '@dealstack/frontend/shared/components/VestingTerms/VestingTermsContext.tsx';
import { orderBy } from 'lodash';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { VestingSharedStyles } from '@dealstack/frontend/shared/components/VestingTerms/VestingTerms.styles.tsx';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

export const TimeBasedVesting = memo(() => {
	const tCommonUi = useTranslationCommonUi();
	const {
		currentVestingTerm,
		handleTimeBasedPercentageUpdate,
		onUpdateVestingTerm,
		onCreateVestingTermGraded,
		onUpdateVestingTermGraded,
		onDeleteVestingTermGraded,
		onCreateVestingTermStraightLine,
		onUpdateVestingTermStraightLine,
	} = useVestingTermsContext();

	const [timeBasedPercentageError, setTimeBasedPercentageError] = useState<string>();
	const [gradedTermCreatorOpen, setGradedTermCreatorOpen] = useState<boolean>(false);
	const [percentageDraft, setPercentageDraft] = useState<number | ''>(
		currentVestingTerm.timeBasedPercentage
	);

	useEffect(() => {
		setTimeBasedPercentageError(undefined);
		setPercentageDraft(currentVestingTerm.timeBasedPercentage);
	}, [currentVestingTerm.id, currentVestingTerm.timeBasedPercentage]);

	const sortedGradedTerms = useMemo(
		() => orderBy(currentVestingTerm.vestingTermGraded, 'duration'),
		[currentVestingTerm.vestingTermGraded]
	);

	const isEnabled = currentVestingTerm.timeBasedPercentage > 0;
	const hasNoUnvestedRoom = !isEnabled && currentVestingTerm.unvestedPercentage === 0;

	return (
		<VestingSharedStyles>
			<VestingCard
				title={tCommonUi('vesting_terms.time_based_vesting_title')}
				titleSize="lg"
				titleWeight={800}
				icon={<IconClockHour3 stroke={2} size={24} />}
				expandable
				expanded={isEnabled}
				onExpandChange={(expanded) => {
					const { error } = handleTimeBasedPercentageUpdate(
						currentVestingTerm.id,
						expanded ? currentVestingTerm.unvestedPercentage : 0
					);
					setTimeBasedPercentageError(error ?? undefined);
				}}
				expandDisabled={hasNoUnvestedRoom}
				expandDisabledTooltip={tCommonUi('vesting_terms.time_based.no_room')}
			>
				<div className="inner">
					<div className="p-3 bg-white border border-gray-200 border-solid rounded">
						<Flex justify="space-between" align="center" className="percentage-row">
							<Text fw={600} c="dark.8" fz="md">
								{tCommonUi('vesting_terms.time_based_vesting_percentage')}
							</Text>

							<div className="percentage-input">
								<NumberInput
									name="timeBasedPercentage"
									value={percentageDraft}
									onChange={(val) =>
										setPercentageDraft(val === '' ? '' : Number(val))
									}
									onBlur={() => {
										if (percentageDraft === '') {
											setPercentageDraft(
												currentVestingTerm.timeBasedPercentage
											);
											setTimeBasedPercentageError(undefined);
											return;
										}
										const { error } = handleTimeBasedPercentageUpdate(
											currentVestingTerm.id,
											percentageDraft
										);
										setTimeBasedPercentageError(error ?? undefined);
									}}
									min={0}
									max={100}
									hideControls
									rightSection={
										<Icon
											variant="percentage"
											size="lg"
											color={defaultTheme.v3.colors.dark[9]}
										/>
									}
									error={timeBasedPercentageError}
								/>
							</div>
						</Flex>

						<div className="flex items-center mb-4">
							<div className="mr-2 text-sm">
								{tCommonUi('vesting_terms.straight_line_label')}
							</div>

							<AdvancedSwitch
								size="m"
								selectedValue={
									currentVestingTerm.type === VestingTermType.STRAIGHT_LINE
								}
								onChange={(event) => {
									onUpdateVestingTerm(currentVestingTerm.id, {
										type: event
											? VestingTermType.STRAIGHT_LINE
											: VestingTermType.GRADED,
									});
								}}
								data-testid={TEST_ELEMENT_IDS.VESTING_TERMS_STRAIGHT_LINE_TOGGLE}
							/>
						</div>

						{currentVestingTerm.type === VestingTermType.STRAIGHT_LINE ? (
							<StraightLine
								vestingTermId={currentVestingTerm.id}
								vestingTermStraightLine={currentVestingTerm.vestingTermStraightLine}
								onUpdateVestingTermStraightLine={onUpdateVestingTermStraightLine}
								onCreateVestingTermStraightLine={onCreateVestingTermStraightLine}
							/>
						) : (
							<GradedTerms
								vestingTermId={currentVestingTerm.id}
								vestingTermGraded={sortedGradedTerms}
								gradedTermCreatorOpen={gradedTermCreatorOpen}
								setGradedTermCreatorOpen={setGradedTermCreatorOpen}
								onCreateVestingTermGraded={onCreateVestingTermGraded}
								onUpdateVestingTermGraded={onUpdateVestingTermGraded}
								onDeleteVestingTermGraded={onDeleteVestingTermGraded}
							/>
						)}
					</div>
				</div>
			</VestingCard>
		</VestingSharedStyles>
	);
});
